import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Upload, Search, Star } from "lucide-react";
import { api, mediaSrc, money, formatApiError } from "@/lib/api";

const EMPTY_ITEM = { title: "", category: "Minifigures", theme: "Other", price: "", condition: "", rarity: "", image_url: "", images: [], description: "", in_stock: true };
const SIMPLE_STATUSES = [["pending", "New"], ["contacted", "In Progress"], ["completed", "Done"]];
const AUTO_STATUS_LABELS = {
  appraised: "Valuation sent ✓",
  countered: "Offer sent — awaiting customer",
  trade_offered: "Offer sent — awaiting customer",
  customer_countered: "Customer replied — action needed",
  accepted: "Accepted — awaiting parcel",
  posted: "Parcel on its way",
  declined: "Declined — closed",
};

const DONE_STATUSES = ["completed", "declined"];
const statusGroup = (s) => (s === "pending" ? "new" : DONE_STATUSES.includes(s) ? "done" : "active");
const orderGroup = (o) => (o.fulfilment === "completed" ? "done" : o.fulfilment === "dispatched" ? "active" : "new");
const STATUS_CHIPS = [["all", "All"], ["new", "New"], ["active", "In Progress"], ["done", "Done"]];

const StatusChips = ({ value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {STATUS_CHIPS.map(([v, l]) => (
      <button
        key={v}
        onClick={() => onChange(v)}
        data-testid={`${testid}-${v}`}
        className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${value === v ? "bg-[#0A0A0A] text-[#F3EDE3] border-[#0A0A0A]" : "border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"}`}
      >
        {l}
      </button>
    ))}
  </div>
);
const THEMES = ["Star Wars", "DC", "Marvel", "POTC", "Retired", "Disney", "Harry Potter", "LOTR", "Other"];
const CONDITION_OPTIONS = [
  "Factory Sealed Box",
  "New — Open Box, Never Built",
  "Mint — Built & Displayed Only",
  "Excellent — Minor Shelf Wear",
  "Very Good — Light Wear",
  "Good — Played With",
  "Fair — Noticeable Wear / Small Damage",
  "Incomplete / For Parts",
];
const RARITY_OPTIONS = ["Popular", "Rare", "Special Piece", "Limited Run", "Ultra Rare", "Grail", "Vault Holy Grail"];

function ItemModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    item
      ? { ...EMPTY_ITEM, ...item, images: item.images?.length ? item.images : item.image_url ? [item.image_url] : [] }
      : EMPTY_ITEM
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [urlDraft, setUrlDraft] = useState("");

  const addUrl = () => {
    const u = urlDraft.trim();
    if (!u) return;
    setForm((f) => ({ ...f, images: [...(f.images || []), u] }));
    setUrlDraft("");
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const uploadImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let done = 0;
    setUploading(`0/${files.length}`);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
        done += 1;
        setUploading(`${done}/${files.length}`);
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded` : "Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err, `Upload failed after ${done} of ${files.length} photos`));
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, image_url: (form.images && form.images[0]) || "" };
      delete payload.condition_custom;
      delete payload.rarity_custom;
      if (item?.id) await api.put(`/admin/items/${item.id}`, payload);
      else await api.post("/admin/items", payload);
      toast.success(item?.id ? "Item updated" : "Item added to the vault");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, type = "text", extra = {}) => (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: type === "checkbox" ? e.target.checked : e.target.value })}
        data-testid={`item-form-${key}-input`}
        className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
        {...extra}
      />
    </div>
  );

  const comboField = (key, label, options) => {
    const customFlag = `${key}_custom`;
    const isCustom = !!form[customFlag] || (form[key] != null && form[key] !== "" && !options.includes(form[key]));
    const selectValue = isCustom ? "__custom__" : (form[key] || "");
    return (
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">{label}</label>
        <select
          value={selectValue}
          onChange={(e) => {
            const v = e.target.value;
            setForm(v === "__custom__"
              ? { ...form, [key]: "", [customFlag]: true }
              : { ...form, [key]: v, [customFlag]: false });
          }}
          data-testid={`item-form-${key}-select`}
          className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
        >
          <option value="">Select…</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value="__custom__">Custom…</option>
        </select>
        {isCustom && (
          <input
            value={form[key] || ""}
            onChange={(e) => setForm({ ...form, [key]: e.target.value, [customFlag]: true })}
            placeholder="Type your own…"
            data-testid={`item-form-${key}-input`}
            className="mt-2 w-full bg-[#F3EDE3] border border-[#B38728] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
          />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="item-modal">
      <div className="bg-white border border-[#DCD3C3] w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl">{item?.id ? "Edit Item" : "Add to the Vault"}</h2>
          <button onClick={onClose} data-testid="item-modal-close-button" className="text-[#5C564C] hover:text-[#0A0A0A]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="mt-6 space-y-4">
          {field("title", "Title", "text", { required: true })}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="item-form-category-select"
                className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
              >
                {["Minifigures", "Sets", "Grails", "Accessories"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">Theme</label>
              <select
                value={form.theme}
                onChange={(e) => setForm({ ...form, theme: e.target.value })}
                data-testid="item-form-theme-select"
                className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
              >
                {THEMES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {field("price", "Price (£)", "number", { required: true, min: "0", step: "0.01" })}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {comboField("condition", "Condition", CONDITION_OPTIONS)}
            {comboField("rarity", "Rarity", RARITY_OPTIONS)}
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">Photos — first one is the cover</label>
            <div className="mt-1 flex gap-2">
              <input
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder="Paste image URL…"
                data-testid="item-form-image-input"
                className="flex-1 bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
              />
              <button
                type="button"
                onClick={addUrl}
                data-testid="item-form-add-url-button"
                className="bg-white border border-[#DCD3C3] px-4 text-xs font-mono uppercase tracking-widest hover:border-[#B38728] hover:text-[#B38728] transition-colors"
              >
                Add
              </button>
              <label className="cursor-pointer bg-[#0A0A0A] text-[#F3EDE3] px-4 flex items-center gap-2 text-xs font-mono uppercase tracking-widest hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors" data-testid="item-form-upload-button">
                <Upload className="w-4 h-4" /> {uploading ? uploading : "Upload"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImage} data-testid="item-form-upload-input" />
              </label>
            </div>
            <p className="text-xs text-[#5C564C] mt-2 font-mono">Pick several photos at once — they upload one after another with a live counter.</p>
            {form.images?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3" data-testid="item-form-photos">
                {form.images.map((u, i) => (
                  <div
                    key={`${u}-${i}`}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragIdx === null || dragIdx === i) return;
                      setForm((f) => {
                        const imgs = [...f.images];
                        const [moved] = imgs.splice(dragIdx, 1);
                        imgs.splice(i, 0, moved);
                        return { ...f, images: imgs };
                      });
                      setDragIdx(null);
                    }}
                    onDragEnd={() => setDragIdx(null)}
                    data-testid={`item-form-photo-${i}`}
                    className={`relative border p-1 bg-white cursor-grab active:cursor-grabbing transition-opacity ${dragIdx === i ? "opacity-40 border-[#B38728]" : "border-[#DCD3C3]"}`}
                    title="Drag to reorder — first photo is the cover"
                  >
                    <img src={mediaSrc(u)} alt="" className="h-20 w-20 object-cover pointer-events-none" />
                    {i === 0 && (
                      <span className="absolute bottom-1.5 left-1.5 bg-[#D4AF37] text-[#0A0A0A] text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5">Cover</span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      data-testid={`item-form-photo-remove-${i}`}
                      className="absolute -top-2 -right-2 bg-[#0A0A0A] text-[#F3EDE3] rounded-full p-0.5 hover:bg-[#B38728] hover:text-[#0A0A0A]"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {form.images?.length > 1 && (
              <p className="text-xs text-[#5C564C] mt-2 font-mono">Drag photos to reorder — the first is always the cover.</p>
            )}
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="item-form-description-input"
              className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
            />
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.in_stock}
              onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
              data-testid="item-form-instock-checkbox"
              className="w-4 h-4 accent-[#B38728]"
            />
            In stock and visible to buyers
          </label>
          <button
            type="submit"
            disabled={saving}
            data-testid="item-submit-button"
            className="w-full bg-[#0A0A0A] text-[#F3EDE3] py-3.5 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Item"}
          </button>
        </form>
      </div>
    </div>
  );
}

function OrderCard({ o, onFulfil }) {
  return (
    <div className="bg-white border border-[#DCD3C3] p-6" data-testid={`order-card-${o.session_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-serif text-lg">{o.item_title}</p>
          <p className="text-xs text-[#5C564C] mt-1 font-mono">{new Date(o.created_at).toLocaleString()} · {o.session_id.slice(-12)}</p>
          <p className={`mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 ${o.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-[#EAE2D5] text-[#5C564C]"}`}>
            {o.payment_status === "paid" ? "Paid" : o.payment_status}
          </p>
        </div>
        <div className="text-right">
          <p className="font-serif text-2xl">£{Number(o.amount || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}</p>
          {o.payment_status === "paid" && (
            <select
              value={o.fulfilment || "to_fulfil"}
              onChange={(e) => onFulfil(o.session_id, e.target.value)}
              data-testid={`order-fulfilment-${o.session_id}`}
              className="mt-2 bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2 text-xs font-mono uppercase tracking-widest focus:outline-none focus:border-[#B38728]"
            >
              <option value="to_fulfil">To Fulfil</option>
              <option value="dispatched">Dispatched</option>
              <option value="completed">Completed</option>
            </select>
          )}
        </div>
      </div>
      {(o.customer_name || o.customer_email || o.customer_address) && (
        <div className="mt-4 border-t border-[#DCD3C3] pt-4 text-sm text-[#5C564C] space-y-1" data-testid={`order-customer-${o.session_id}`}>
          {o.customer_name && <p><span className="text-[#B38728] font-mono text-xs uppercase">Customer:</span> {o.customer_name}</p>}
          {o.customer_email && <p><span className="text-[#B38728] font-mono text-xs uppercase">Email:</span> <a className="text-[#B38728] hover:underline" href={`mailto:${o.customer_email}`}>{o.customer_email}</a></p>}
          {o.customer_address && (
            <p><span className="text-[#B38728] font-mono text-xs uppercase">Address:</span> {[o.customer_address.line1, o.customer_address.line2, o.customer_address.city, o.customer_address.postal_code, o.customer_address.country].filter(Boolean).join(", ")}</p>
          )}
        </div>
      )}
    </div>
  );
}

function SubmissionCard({ sub, onStatus, onRespond }) {
  const [amount, setAmount] = useState("");
  const [bonus, setBonus] = useState("10");
  const [note, setNote] = useState("");
  const [finalOffer, setFinalOffer] = useState(false);
  const [tradeMode, setTradeMode] = useState(false);
  const [sending, setSending] = useState("");
  const [viewIdx, setViewIdx] = useState(null);
  const tradeTotal = amount ? (parseFloat(amount) * (1 + (parseFloat(bonus) || 0) / 100)) : 0;

  const respond = (action) => {
    setSending(action);
    onRespond(sub.id, { action, amount: amount ? parseFloat(amount) : undefined, bonus_pct: action === "trade" && bonus ? parseFloat(bonus) : undefined, note: note || undefined, final: (action === "counter" || action === "trade") && finalOffer ? true : undefined })
      .finally(() => setSending(""));
  };

  const inputCls = "bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]";
  const btnCls = "px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.15em] transition-colors disabled:opacity-50";

  return (
    <div className="bg-white border border-[#DCD3C3] p-6" data-testid={`submission-card-${sub.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-serif text-lg">{sub.item_title || sub.details?.slice(0, 60) || "Submission"}</p>
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#B38728] mt-1" data-testid={`submission-ticket-${sub.id}`}>
            {sub.ticket}{sub.reference ? ` · RE: ${sub.reference}` : ""}
          </p>
          <p className="text-sm text-[#5C564C] mt-1">{sub.name} · <a className="text-[#B38728] hover:underline" href={`mailto:${sub.email}`}>{sub.email}</a>{sub.phone ? ` · ${sub.phone}` : ""}</p>
          <p className="text-xs text-[#5C564C] mt-1 font-mono">{new Date(sub.created_at).toLocaleString()}</p>
        </div>
        <select
          value={sub.status}
          onChange={(e) => { if (e.target.value !== sub.status) onStatus(sub.id, e.target.value); }}
          data-testid={`submission-status-${sub.id}`}
          className="bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2 text-xs font-mono uppercase tracking-widest focus:outline-none focus:border-[#B38728]"
        >
          {AUTO_STATUS_LABELS[sub.status] && <option value={sub.status}>{AUTO_STATUS_LABELS[sub.status]}</option>}
          {SIMPLE_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="mt-4 text-sm text-[#5C564C] space-y-1">
        {sub.current_offer && (
          <p className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-3 py-2" data-testid={`current-offer-${sub.id}`}>
            <span className="text-[#B38728] font-mono text-xs uppercase">Offer on the table:</span>{" "}
            £{Number(sub.current_offer.amount).toLocaleString("en-GB")}
            {sub.current_offer.bonus_pct ? ` + ${sub.current_offer.bonus_pct}% trade bonus` : ""}
            {sub.current_offer.final && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#B38728] border border-[#B38728] px-1.5 py-0.5">Final</span>}
          </p>
        )}
        {sub.reason && <p><span className="text-[#B38728] font-mono text-xs uppercase">Reason:</span> {sub.reason}</p>}
        {sub.order_ref && <p><span className="text-[#B38728] font-mono text-xs uppercase">Order Ref:</span> {sub.order_ref}</p>}
        {sub.asking_price && <p><span className="text-[#B38728] font-mono text-xs uppercase">Asking:</span> {/^\d/.test(String(sub.asking_price)) ? `£${sub.asking_price}` : sub.asking_price}</p>}
        {sub.trade_preference && <p><span className="text-[#B38728] font-mono text-xs uppercase">Prefers:</span> {sub.trade_preference}</p>}
        {sub.era && <p><span className="text-[#B38728] font-mono text-xs uppercase">Era:</span> {sub.era}</p>}
        {sub.condition_notes && <p>{sub.condition_notes}</p>}
        {sub.details && sub.item_title == null && <p>{sub.details}</p>}
      </div>
      {sub.history?.length > 1 && (
        <div className="mt-4 border-t border-[#DCD3C3] pt-3 space-y-1.5" data-testid={`history-${sub.id}`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-1">Negotiation trail</p>
          {sub.history.map((h, i) => (
            <p key={i} className="text-xs font-mono text-[#5C564C]">
              <span className={h.actor === "stud" ? "text-[#B38728]" : "text-[#0A0A0A]"}>{h.actor === "stud" ? "YOU" : "CUSTOMER"}</span>
              {" — "}
              {h.action === "submitted" && "submitted the offer"}
              {h.action === "appraise" && `sent valuation £${h.amount}`}
              {h.action === "counter" && `countered £${h.amount}`}
              {h.action === "trade" && `offered trade credit £${h.amount}${h.bonus_pct ? ` + ${h.bonus_pct}% bonus` : ""}`}
              {h.action === "accept" && "accepted"}
              {h.action === "posted" && "marked the parcel as posted"}
              {h.action === "payout_submitted" && "sent their bank details"}
              {h.action === "paid" && "marked payment as sent"}
              {h.action === "verify" && "verified — payment released"}
              {(h.action === "decline" || h.action === "decline_return") && "declined"}
              {h.action === "approve" && "approved the return"}
              {" "}
              <span className="opacity-50">· {new Date(h.at).toLocaleDateString()}</span>
            </p>
          ))}
        </div>
      )}
      {sub.files?.length > 0 && (        <div className="mt-4 flex flex-wrap gap-3">
          {sub.files.map((f, i) =>
            (f.content_type || "").startsWith("image/") ? (
              <button
                key={f.path}
                onClick={() => setViewIdx(i)}
                data-testid={`submission-file-${f.path}`}
                className="border border-[#DCD3C3] p-0.5 hover:border-[#B38728] transition-colors cursor-zoom-in"
                title="Click to enlarge"
              >
                <img src={mediaSrc(`/api/files/${f.path}`)} alt={f.filename} className="h-20 w-20 object-cover" />
              </button>
            ) : (
              <button
                key={f.path}
                onClick={() => setViewIdx(i)}
                className="text-xs font-mono text-[#B38728] underline"
                data-testid={`submission-file-${f.path}`}
              >
                {f.filename}
              </button>
            )
          )}
        </div>
      )}

      {viewIdx !== null && sub.files?.[viewIdx] && (
        <div
          className="fixed inset-0 z-[70] bg-[#0A0A0A]/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setViewIdx(null)}
          data-testid="attachment-viewer"
        >
          <button
            onClick={() => setViewIdx(null)}
            data-testid="attachment-viewer-close"
            aria-label="Close viewer"
            className="absolute top-5 right-5 text-[#F3EDE3] hover:text-[#D4AF37] transition-colors z-10"
          >
            <X className="w-7 h-7" />
          </button>
          {sub.files.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setViewIdx((i) => (i - 1 + sub.files.length) % sub.files.length); }}
                data-testid="attachment-prev"
                aria-label="Previous attachment"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-[#F3EDE3]/10 border border-[#F3EDE3]/30 text-[#F3EDE3] p-3 hover:bg-[#D4AF37] hover:text-[#0A0A0A] hover:border-[#D4AF37] transition-colors z-10"
              >
                ‹
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setViewIdx((i) => (i + 1) % sub.files.length); }}
                data-testid="attachment-next"
                aria-label="Next attachment"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-[#F3EDE3]/10 border border-[#F3EDE3]/30 text-[#F3EDE3] p-3 hover:bg-[#D4AF37] hover:text-[#0A0A0A] hover:border-[#D4AF37] transition-colors z-10"
              >
                ›
              </button>
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F3EDE3]/80 z-10" data-testid="attachment-counter">
                {viewIdx + 1} / {sub.files.length}
              </span>
            </>
          )}
          {(sub.files[viewIdx].content_type || "").startsWith("image/") ? (
            <img
              src={mediaSrc(`/api/files/${sub.files[viewIdx].path}`)}
              alt={sub.files[viewIdx].filename}
              className="max-w-full max-h-[85vh] object-contain border border-[#B38728]/40 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <video
              src={mediaSrc(`/api/files/${sub.files[viewIdx].path}`)}
              controls
              autoPlay
              className="max-w-full max-h-[85vh] border border-[#B38728]/40 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}

      {sub.response && (
        <div className="mt-5 border-t border-[#DCD3C3] pt-4" data-testid={`response-summary-${sub.id}`}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#B38728]">
            Replied: {sub.response.action.replace("_", " ")}
            {sub.response.amount != null && ` — £${Number(sub.response.amount).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`}
            {sub.response.action === "trade" && sub.response.bonus_pct != null && ` + ${sub.response.bonus_pct}% bonus`}
          </p>
          <p className="text-xs text-[#5C564C] mt-1">Sent {new Date(sub.response.sent_at).toLocaleString()}{sub.response.note ? ` · PS: "${sub.response.note}"` : ""}</p>
        </div>
      )}
      {(sub.kind === "offer" ? !["accepted", "posted", "completed", "declined"].includes(sub.status) : !sub.response) && (
        <div className="mt-5 border-t border-[#DCD3C3] pt-4" data-testid={`respond-panel-${sub.id}`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-3">
            {sub.kind === "valuation" ? "Send Valuation Reply" : sub.kind === "return" ? "Respond to Return" : "Respond to Offer"}
          </p>
          {sub.kind !== "return" && (
            <div className="flex flex-wrap gap-3">
              <input
                type="number" min="0" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={sub.kind === "valuation" ? "Appraised value (£)" : "Amount (£)"}
                data-testid={`respond-amount-${sub.id}`}
                className={`${inputCls} w-40`}
              />
              {sub.kind === "offer" && tradeMode && (
                <div className="flex items-center gap-1">
                  <input
                    type="number" min="0" step="1"
                    value={bonus}
                    onChange={(e) => setBonus(e.target.value)}
                    data-testid={`respond-bonus-${sub.id}`}
                    className={`${inputCls} w-20`}
                  />
                  <span className="text-xs text-[#5C564C] font-mono">% trade bonus</span>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-3">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional personal note (PS in the email)"
              data-testid={`respond-note-${sub.id}`}
              className={`${inputCls} flex-1 min-w-48`}
            />
          </div>
          {sub.kind === "offer" && tradeMode && amount && (
            <p className="text-xs text-[#5C564C] mt-2 font-mono" data-testid={`trade-total-${sub.id}`}>
              Trade credit total: £{tradeTotal.toLocaleString("en-GB", { maximumFractionDigits: 2 })} (£{parseFloat(amount).toLocaleString("en-GB")} + {bonus || 0}% bonus)
            </p>
          )}
          {sub.kind === "offer" && (
            <label className="mt-3 flex items-center gap-2.5 text-xs text-[#5C564C] cursor-pointer select-none" data-testid={`offer-final-label-${sub.id}`}>
              <input
                type="checkbox"
                checked={finalOffer}
                onChange={(e) => setFinalOffer(e.target.checked)}
                data-testid={`offer-final-${sub.id}`}
                className="w-4 h-4 accent-[#B38728]"
              />
              Final offer — the customer can only accept or decline, no further counters
            </label>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {sub.kind === "valuation" && (
              <button
                onClick={() => respond("appraise")}
                disabled={!amount || !!sending}
                data-testid={`send-valuation-${sub.id}`}
                className={`${btnCls} bg-[#0A0A0A] text-[#F3EDE3] hover:bg-[#B38728] hover:text-[#0A0A0A]`}
              >
                {sending === "appraise" ? "Sending…" : "Send Valuation"}
              </button>
            )}
            {sub.kind === "offer" && (
              <>
                <button onClick={() => respond("accept")} disabled={!!sending} data-testid={`offer-accept-${sub.id}`} className={`${btnCls} bg-[#0A0A0A] text-[#F3EDE3] hover:bg-green-800`}>
                  {sending === "accept" ? "Sending…" : "Accept"}
                </button>
                <button onClick={() => respond("counter")} disabled={!amount || !!sending} data-testid={`offer-counter-${sub.id}`} className={`${btnCls} border border-[#B38728] text-[#B38728] hover:bg-[#B38728] hover:text-[#0A0A0A]`}>
                  {sending === "counter" ? "Sending…" : "Counter"}
                </button>
                {!tradeMode ? (
                  <button onClick={() => setTradeMode(true)} disabled={!!sending} data-testid={`offer-trade-${sub.id}`} className={`${btnCls} border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F3EDE3]`}>
                    Offer Trade Credit
                  </button>
                ) : (
                  <>
                    <button onClick={() => respond("trade")} disabled={!amount || !!sending} data-testid={`offer-trade-send-${sub.id}`} className={`${btnCls} border border-[#0A0A0A] bg-[#0A0A0A] text-[#F3EDE3] hover:bg-[#B38728] hover:text-[#0A0A0A]`}>
                      {sending === "trade" ? "Sending…" : "Send Trade Offer"}
                    </button>
                    <button onClick={() => setTradeMode(false)} data-testid={`offer-trade-cancel-${sub.id}`} className={`${btnCls} border border-[#DCD3C3] text-[#5C564C] hover:border-[#0A0A0A]`}>
                      Cancel
                    </button>
                  </>
                )}
                <button onClick={() => respond("decline")} disabled={!!sending} data-testid={`offer-decline-${sub.id}`} className={`${btnCls} border border-[#DCD3C3] text-[#5C564C] hover:border-red-700 hover:text-red-700`}>
                  {sending === "decline" ? "Sending…" : "Decline"}
                </button>
              </>
            )}
            {sub.kind === "return" && (
              <>
                <button onClick={() => respond("approve")} disabled={!!sending} data-testid={`return-approve-${sub.id}`} className={`${btnCls} bg-[#0A0A0A] text-[#F3EDE3] hover:bg-green-800`}>
                  {sending === "approve" ? "Sending…" : "Approve Return"}
                </button>
                <button onClick={() => respond("decline_return")} disabled={!!sending} data-testid={`return-decline-${sub.id}`} className={`${btnCls} border border-[#DCD3C3] text-[#5C564C] hover:border-red-700 hover:text-red-700`}>
                  {sending === "decline_return" ? "Sending…" : "Decline Return"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {sub.payout_details && (
        <div className="mt-4 border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-3" data-testid={`payout-details-${sub.id}`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-1">Bank details for payment</p>
          <p className="text-sm text-[#0A0A0A] font-mono">
            {sub.payout_details.account_name} · {String(sub.payout_details.sort_code).replace(/(\d{2})(\d{2})(\d{2})/, "$1-$2-$3")} · {sub.payout_details.account_number}
          </p>
        </div>
      )}

      {sub.credit_code && (
        <p className="mt-4 border border-[#B38728] bg-[#FDF9EF] px-4 py-3 text-sm" data-testid={`credit-issued-${sub.id}`}>
          <span className="text-[#B38728] font-mono text-xs uppercase">Store credit issued:</span>{" "}
          <span className="font-mono tracking-widest">{sub.credit_code}</span>
          <span className="text-[#5C564C]"> — live balance in the Credits tab</span>
        </p>
      )}

      {sub.kind === "offer" && ["accepted", "posted"].includes(sub.status) && (
        <div className="mt-5 border-t border-[#DCD3C3] pt-4" data-testid={`fulfil-panel-${sub.id}`}>
          {sub.tracking_number && (
            <p className="text-sm text-[#5C564C] mb-3" data-testid={`tracking-${sub.id}`}>
              <span className="text-[#B38728] font-mono text-xs uppercase">Tracking:</span> {sub.tracking_number}
            </p>
          )}
          <button
            onClick={() => respond("verify")}
            disabled={!!sending}
            data-testid={`offer-verify-${sub.id}`}
            className={`${btnCls} bg-green-800 text-[#F3EDE3] hover:bg-[#0A0A0A]`}
          >
            {sending === "verify" ? "Sending…" : "Verify & Complete"}
          </button>
          <p className="text-xs text-[#5C564C] mt-2">
            Click once the parcel has arrived and passed your checks — the customer gets a payment-released email automatically.
          </p>
        </div>
      )}

      {sub.kind === "offer" && sub.status === "completed" && sub.deal_kind !== "credit" && (
        <div className="mt-5 border-t border-[#DCD3C3] pt-4" data-testid={`payment-panel-${sub.id}`}>
          {sub.paid_at ? (
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-green-800" data-testid={`paid-chip-${sub.id}`}>
              Paid ✓ · {new Date(sub.paid_at).toLocaleString()}
            </p>
          ) : (
            <>
              <button
                onClick={() => respond("paid")}
                disabled={!!sending}
                data-testid={`offer-paid-${sub.id}`}
                className={`${btnCls} bg-[#0A0A0A] text-[#F3EDE3] hover:bg-green-800`}
              >
                {sending === "paid" ? "Sending…" : "Mark as Paid"}
              </button>
              <p className="text-xs text-[#5C564C] mt-2">
                Send the bank transfer from your banking app first, then click — the customer gets a payment-sent email automatically.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inventory");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [offers, setOffers] = useState([]);
  const [returns, setReturns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [credits, setCredits] = useState([]);
  const [subQuery, setSubQuery] = useState("");
  const [valFilter, setValFilter] = useState("all");
  const [offFilter, setOffFilter] = useState("all");
  const [retFilter, setRetFilter] = useState("all");
  const [ordFilter, setOrdFilter] = useState("all");
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [modal, setModal] = useState(null);

  const matchSub = (s) => {
    const q = subQuery.trim().toLowerCase();
    if (!q) return true;
    return [s.ticket, s.reference, s.name, s.email, s.item_title, s.details, s.condition_notes, s.reason, s.order_ref]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q);
  };
  const filteredValuations = valuations.filter(matchSub);
  const filteredOffers = offers.filter(matchSub);
  const filteredReturns = returns.filter(matchSub);

  const renderSubs = (list, groupFilter, emptyText) => {
    const filtered = groupFilter === "all" ? list : list.filter((s) => statusGroup(s.status) === groupFilter);
    const active = filtered.filter((s) => statusGroup(s.status) !== "done");
    const done = filtered.filter((s) => statusGroup(s.status) === "done");
    return (
      <>
        {active.map((s) => <SubmissionCard key={s.id} sub={s} onStatus={setStatus} onRespond={respond} />)}
        {done.length > 0 && (
          <div className="pt-6 border-t border-[#DCD3C3]" data-testid="completed-section">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C564C] mb-4">Completed ({done.length})</p>
            <div className="space-y-4 opacity-70">
              {done.map((s) => <SubmissionCard key={s.id} sub={s} onStatus={setStatus} onRespond={respond} />)}
            </div>
          </div>
        )}
        {filtered.length === 0 && (
          <p className="text-sm text-[#5C564C] py-10 text-center">{subQuery || groupFilter !== "all" ? "No requests match." : emptyText}</p>
        )}
      </>
    );
  };

  const load = async () => {
    try {
      const [s, i, v, o, r, or, cr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/items"),
        api.get("/admin/submissions", { params: { kind: "valuation" } }),
        api.get("/admin/submissions", { params: { kind: "offer" } }),
        api.get("/admin/submissions", { params: { kind: "return" } }),
        api.get("/admin/orders"),
        api.get("/admin/credits"),
      ]);
      setStats(s.data);
      setItems(i.data);
      setValuations(v.data);
      setOffers(o.data);
      setReturns(r.data);
      setOrders(or.data);
      setCredits(cr.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("sx_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err, "Failed to load dashboard"));
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("sx_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteItem = async (id) => {
    if (!window.confirm("Remove this item from the vault?")) return;
    await api.delete(`/admin/items/${id}`);
    toast.success("Item removed");
    load();
  };

  const setStatus = async (id, status) => {
    await api.patch(`/admin/submissions/${id}`, { status });
    toast.success(`Marked as ${status}`);
    load();
  };

  const respond = async (id, body) => {
    try {
      await api.post(`/admin/submissions/${id}/respond`, body);
      toast.success("Response sent to the customer");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not send the response"));
    }
  };

  const setFulfilment = async (sessionId, fulfilment) => {
    try {
      await api.patch(`/admin/orders/${sessionId}`, { fulfilment });
      toast.success("Order updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not update the order"));
    }
  };

  const toggleFeature = async (item) => {
    try {
      await api.post(`/admin/items/${item.id}/feature`);
      toast.success(item.is_featured ? `${item.title} removed from the homepage` : `${item.title} is now the homepage hero`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not update the homepage feature"));
    }
  };

  const logout = () => {
    localStorage.removeItem("sx_admin_token");
    navigate("/admin/login");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error("New passwords don't match");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated — use it next time you log in");
      setPwOpen(false);
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (err) {
      toast.error(formatApiError(err, "Could not update password"));
    } finally {
      setPwBusy(false);
    }
  };

  const kpi = (label, value, testId) => (
    <div className="bg-white border border-[#DCD3C3] p-5" data-testid={testId}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728]">{label}</p>
      <p className="font-serif text-3xl mt-2">{value}</p>
    </div>
  );

  return (
    <main className="min-h-screen bg-[#F3EDE3] text-[#0A0A0A]" data-testid="admin-dashboard">
      <header className="border-b border-[#DCD3C3] bg-[#F3EDE3]/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B38728]">Stud Exchange</p>
            <h1 className="font-serif text-2xl">Vault Office</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs font-mono uppercase tracking-widest text-[#5C564C] hover:text-[#B38728] transition-colors" data-testid="admin-storefront-link">Storefront</Link>
            <button
              onClick={() => setPwOpen(true)}
              data-testid="change-password-button"
              className="text-xs font-mono uppercase tracking-widest text-[#5C564C] hover:text-[#B38728] transition-colors"
            >
              Password
            </button>
            <button onClick={logout} data-testid="logout-button" className="bg-[#0A0A0A] text-[#F3EDE3] px-5 py-2.5 text-xs font-mono uppercase tracking-widest hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpi("Stock Value", money(stats.stock_value), "kpi-stock-value")}
            {kpi("Items In Stock", `${stats.items_in_stock} / ${stats.items_total}`, "kpi-items")}
            {kpi("Pending Requests", stats.pending_valuations + stats.pending_offers, "kpi-pending")}
            {kpi("Revenue", money(stats.revenue), "kpi-revenue")}
          </div>
        )}

        <div className="mt-8 flex gap-2 border-b border-[#DCD3C3]">
          {[["inventory", "Inventory"], ["valuations", `Valuations (${valuations.filter((v) => v.status === "pending").length})`], ["offers", `Offers (${offers.filter((o) => ["pending", "customer_countered"].includes(o.status)).length})`], ["returns", `Returns (${returns.filter((r) => r.status === "pending").length})`], ["orders", `Orders (${orders.filter((o) => o.payment_status === "paid" && o.fulfilment !== "completed").length})`], ["credits", `Credits (${credits.filter((c) => c.remaining > 0).length})`]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === key ? "border-[#B38728] text-[#0A0A0A]" : "border-transparent text-[#5C564C] hover:text-[#0A0A0A]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "inventory" && (
          <div className="mt-6">
            <button onClick={() => setModal(EMPTY_ITEM)} data-testid="add-item-button" className="flex items-center gap-2 bg-[#0A0A0A] text-[#F3EDE3] px-5 py-3 text-xs font-mono uppercase tracking-widest hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors">
              <Plus className="w-4 h-4" /> Add Item
            </button>
            <div className="mt-5 space-y-3">
              {items.map((item) => (
                <div key={item.id} className="bg-white border border-[#DCD3C3] p-4 flex items-center gap-4" data-testid={`admin-item-${item.id}`}>
                  {item.image_url ? (
                    <img src={mediaSrc(item.image_url)} alt="" className="w-16 h-16 object-cover border border-[#DCD3C3]" />
                  ) : (
                    <div className="w-16 h-16 bg-[#EAE2D5] border border-[#DCD3C3]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-serif truncate">
                      {item.title}
                      {item.is_featured && (
                        <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#B38728] border border-[#B38728] px-1.5 py-0.5 align-middle" data-testid={`featured-badge-${item.id}`}>
                          On homepage
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#5C564C] font-mono mt-1">
                      {item.category}{item.theme ? ` · ${item.theme}` : ""} · {money(item.price)} · {item.in_stock ? <span className="text-green-700">IN STOCK</span> : <span className="text-red-700">SOLD / HIDDEN</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleFeature(item)}
                    data-testid={`feature-item-${item.id}`}
                    title={item.is_featured ? "Remove from homepage hero" : "Show on homepage hero"}
                    className={`p-2.5 border transition-colors ${item.is_featured ? "border-[#B38728] text-[#B38728] bg-[#D4AF37]/10" : "border-[#DCD3C3] hover:border-[#B38728] hover:text-[#B38728]"}`}
                  >
                    <Star className="w-4 h-4" fill={item.is_featured ? "currentColor" : "none"} />
                  </button>
                  <button onClick={() => setModal(item)} data-testid={`edit-item-${item.id}`} className="p-2.5 border border-[#DCD3C3] hover:border-[#B38728] hover:text-[#B38728] transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteItem(item.id)} data-testid={`delete-item-${item.id}`} className="p-2.5 border border-[#DCD3C3] hover:border-red-700 hover:text-red-700 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-[#5C564C] py-10 text-center">The vault is empty. Add your first piece.</p>}
            </div>
          </div>
        )}

        {tab === "valuations" && (
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative flex-1 min-w-[240px] max-w-sm">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#B38728] pointer-events-none" />
                <input
                  value={subQuery}
                  onChange={(e) => setSubQuery(e.target.value)}
                  placeholder="Search by ticket, name, email or item…"
                  data-testid="submission-search-input"
                  className="w-full bg-white border border-[#DCD3C3] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
                />
              </div>
              <StatusChips value={valFilter} onChange={setValFilter} testid="val-status-filter" />
            </div>
            {renderSubs(filteredValuations, valFilter, "No valuation requests yet.")}
          </div>
        )}

        {tab === "returns" && (
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative flex-1 min-w-[240px] max-w-sm">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#B38728] pointer-events-none" />
                <input
                  value={subQuery}
                  onChange={(e) => setSubQuery(e.target.value)}
                  placeholder="Search by ticket, name, email or item…"
                  data-testid="submission-search-input"
                  className="w-full bg-white border border-[#DCD3C3] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
                />
              </div>
              <StatusChips value={retFilter} onChange={setRetFilter} testid="ret-status-filter" />
            </div>
            {renderSubs(filteredReturns, retFilter, "No return requests yet.")}
          </div>
        )}

        {tab === "orders" && (          <div className="mt-6 space-y-4" data-testid="orders-tab">
            <StatusChips value={ordFilter} onChange={setOrdFilter} testid="ord-status-filter" />
            {(() => {
              const list = ordFilter === "all" ? orders : orders.filter((o) => orderGroup(o) === ordFilter);
              const active = list.filter((o) => orderGroup(o) !== "done");
              const done = list.filter((o) => orderGroup(o) === "done");
              return (
                <>
                  {active.map((o) => <OrderCard key={o.session_id} o={o} onFulfil={setFulfilment} />)}
                  {done.length > 0 && (
                    <div className="pt-6 border-t border-[#DCD3C3]" data-testid="orders-completed-section">
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C564C] mb-4">Completed ({done.length})</p>
                      <div className="space-y-4 opacity-70">
                        {done.map((o) => <OrderCard key={o.session_id} o={o} onFulfil={setFulfilment} />)}
                      </div>
                    </div>
                  )}
                  {list.length === 0 && <p className="text-sm text-[#5C564C] py-10 text-center">{ordFilter !== "all" ? "No orders in this state." : "No orders yet — they'll appear here the moment a payment lands."}</p>}
                </>
              );
            })()}
          </div>
        )}

        {tab === "credits" && (
          <div className="mt-6 space-y-4" data-testid="credits-tab">
            {credits.map((c) => (
              <div key={c.id} className="bg-white border border-[#DCD3C3] p-6" data-testid={`credit-card-${c.code}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-lg tracking-[0.25em]">{c.code}</p>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-[#B38728] mt-1">{c.ticket}</p>
                    <p className="text-sm text-[#5C564C] mt-1">{c.name} · <a className="text-[#B38728] hover:underline" href={`mailto:${c.email}`}>{c.email}</a></p>
                    <p className="text-xs text-[#5C564C] mt-1 font-mono">Issued {new Date(c.created_at).toLocaleString()} · never expires</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-2xl" data-testid={`credit-remaining-${c.code}`}>£{Number(c.remaining || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}</p>
                    <p className="text-xs text-[#5C564C] font-mono mt-1">of £{Number(c.amount || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })} issued</p>
                    <p className={`mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 ${c.remaining > 0 ? "bg-green-100 text-green-800" : "bg-[#EAE2D5] text-[#5C564C]"}`}>
                      {c.remaining > 0 ? "Active" : "Fully used"}
                    </p>
                  </div>
                </div>
                {c.uses?.length > 0 && (
                  <div className="mt-4 border-t border-[#DCD3C3] pt-3 space-y-1.5" data-testid={`credit-uses-${c.code}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-1">Redemptions</p>
                    {c.uses.map((u, i) => (
                      <p key={i} className="text-xs font-mono text-[#5C564C]">
                        −£{Number(u.amount).toLocaleString("en-GB", { maximumFractionDigits: 2 })}
                        <span className="opacity-50"> · order {String(u.session_id).slice(-12)} · {new Date(u.at).toLocaleDateString()}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {credits.length === 0 && <p className="text-sm text-[#5C564C] py-10 text-center">No store credit issued yet — codes are created automatically when you Verify &amp; Complete a trade-credit deal.</p>}
          </div>
        )}

        {tab === "offers" && (          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative flex-1 min-w-[240px] max-w-sm">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#B38728] pointer-events-none" />
                <input
                  value={subQuery}
                  onChange={(e) => setSubQuery(e.target.value)}
                  placeholder="Search by ticket, name, email or item…"
                  data-testid="submission-search-input"
                  className="w-full bg-white border border-[#DCD3C3] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]"
                />
              </div>
              <StatusChips value={offFilter} onChange={setOffFilter} testid="off-status-filter" />
            </div>
            {renderSubs(filteredOffers, offFilter, "No offers yet.")}
          </div>
        )}
      </div>

      {modal && <ItemModal item={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={load} />}

      {pwOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !pwBusy && setPwOpen(false)} data-testid="change-password-modal">
          <form onSubmit={changePassword} onClick={(e) => e.stopPropagation()} className="bg-white border border-[#DCD3C3] p-8 w-full max-w-md">
            <h3 className="font-serif text-2xl">Change Password</h3>
            <p className="text-sm text-[#5C564C] mt-1 mb-6">You stay logged in on this device — use the new password next time you log in elsewhere.</p>
            <div className="space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">Current password</label>
                <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} required data-testid="change-password-current-input"
                  className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">New password (10+ characters)</label>
                <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} required minLength={10} data-testid="change-password-new-input"
                  className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C]">Repeat new password</label>
                <input type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} required data-testid="change-password-confirm-input"
                  className="mt-1 w-full bg-[#F3EDE3] border border-[#DCD3C3] px-3 py-2.5 text-sm focus:outline-none focus:border-[#B38728]" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" disabled={pwBusy} data-testid="change-password-submit"
                className="flex-1 bg-[#0A0A0A] text-[#F3EDE3] py-3 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50">
                {pwBusy ? "Updating…" : "Update Password"}
              </button>
              <button type="button" onClick={() => setPwOpen(false)} disabled={pwBusy} data-testid="change-password-cancel"
                className="px-5 border border-[#DCD3C3] font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C564C] hover:border-[#0A0A0A] transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
